import React from 'react';
import { Avatar } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * ProfilePicture component for displaying user/staff profile pictures
 *
 * @param {Object} props
 * @param {string|null} props.profilePictureFileId - The file ID of the profile picture
 * @param {string} props.name - Name of the person (used for initials fallback)
 * @param {number} props.size - Size of the avatar in pixels (default: 40)
 * @param {string} props.imageSize - Size parameter for the image URL (default: 64x64)
 * @param {Object} props.sx - Additional MUI sx props
 * @param {string} props.bgcolor - Background color for the avatar (default: 'primary.main')
 * @returns {JSX.Element}
 */
export default function ProfilePicture({
  profilePictureFileId,
  name = '',
  size = 40,
  imageSize = '64x64',
  sx = {},
  color = null,
  bgcolor = 'primary.main',
  ...otherProps
}) {
  // Generate profile picture URL if available
  const profilePictureUrl = profilePictureFileId
    ? `${API_BASE_URL}/api/v1/file/files/download/${profilePictureFileId}?size=${imageSize}`
    : null;

  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const initials = getInitials(name);

  return (
    <Avatar
      src={profilePictureUrl}
      sx={{
        bgcolor: color ?? bgcolor,
        width: size,
        height: size,
        fontSize: size > 40 ? '1rem' : '0.875rem',
        fontWeight: 600,
        ...sx,
      }}
      {...otherProps}
    >
      {initials || <PersonIcon sx={{ fontSize: size > 40 ? '1.5rem' : '1rem' }} />}
    </Avatar>
  );
}
